import { useState, useEffect } from 'react';
import { api, invalidateCached, resolveImageUrl } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Upload, X } from 'lucide-react';
import { FALLBACK_ACTIVITIES_CONTENT } from '@/lib/publicPageData';

export default function AdminContent() {
  const [home, setHome] = useState(null);
  const [about, setAbout] = useState(null);
  const [faith, setFaith] = useState(null);
  const [activities, setActivities] = useState(null);
  const [saving, setSaving] = useState('');
  const [uploadingWelcomeImage, setUploadingWelcomeImage] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/content/home'),
      api.get('/content/about'),
      api.get('/content/faith'),
      api.get('/content/activities').catch(() => ({ data: FALLBACK_ACTIVITIES_CONTENT })),
    ]).then(([h, a, f, act]) => {
      setHome(h.data);
      setAbout(a.data);
      setFaith(f.data);
      setActivities(act.data);
    }).catch(() => toast.error('Failed to load content'));
  }, []);

  const saveSection = async (key, data) => {
    setSaving(key);
    try {
      await api.put(`/admin/content/${key}`, data);
      invalidateCached(`/content/${key}`);
      toast.success('Content saved successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save content');
    } finally {
      setSaving('');
    }
  };

  const handleWelcomeImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !home) return;

    setUploadingWelcomeImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/admin/upload', formData);
      setHome({ ...home, welcome_image: res.data.storage_path });
      toast.success('Welcome image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Welcome image upload failed');
    } finally {
      setUploadingWelcomeImage(false);
      e.target.value = '';
    }
  };

  const handleRemoveWelcomeImage = () => {
    setHome({ ...home, welcome_image: '' });
    toast.success('Welcome image removed. Save Home Content to publish this change.');
  };

  const updateActivity = (index, field, value) => {
    const next = [...(activities.activities || [])];
    next[index] = { ...next[index], [field]: value };
    setActivities({ ...activities, activities: next });
  };

  if (!home || !about || !faith || !activities) {
    return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-3 border-[#1E4FA1] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="admin-content-page">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Site Content
        </h1>
        <p className="text-[#94A3B8] text-sm mt-1">Edit all text and content across the website</p>
      </div>

      <Tabs defaultValue="home" className="w-full">
        <TabsList className="mb-6 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-1 flex-wrap h-auto">
          <TabsTrigger value="home" data-testid="tab-home" className="rounded-lg">Home Page</TabsTrigger>
          <TabsTrigger value="about" data-testid="tab-about" className="rounded-lg">About Page</TabsTrigger>
          <TabsTrigger value="faith" data-testid="tab-faith" className="rounded-lg">Statement of Faith</TabsTrigger>
          <TabsTrigger value="activities" data-testid="tab-activities" className="rounded-lg">Activities</TabsTrigger>
        </TabsList>

        {/* HOME PAGE */}
        <TabsContent value="home">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 space-y-6">
            <h2 className="text-lg font-bold text-[#0F172A]">Hero Section</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1">Title</label>
                <Input data-testid="home-hero-title" value={home.hero_title} onChange={(e) => setHome({ ...home, hero_title: e.target.value })} className="rounded-xl" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1">CTA Text</label>
                <Input data-testid="home-hero-cta" value={home.hero_cta_text} onChange={(e) => setHome({ ...home, hero_cta_text: e.target.value })} className="rounded-xl" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Subtitle</label>
              <Textarea data-testid="home-hero-subtitle" value={home.hero_subtitle} onChange={(e) => setHome({ ...home, hero_subtitle: e.target.value })} className="rounded-xl" rows={2} />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Hero Background Image URL</label>
              <Input data-testid="home-hero-bg" value={home.hero_bg_image} onChange={(e) => setHome({ ...home, hero_bg_image: e.target.value })} className="rounded-xl" />
            </div>

            <hr className="border-[#E2E8F0]" />
            <h2 className="text-lg font-bold text-[#0F172A]">Welcome Section</h2>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Welcome Title</label>
              <Input data-testid="home-welcome-title" value={home.welcome_title} onChange={(e) => setHome({ ...home, welcome_title: e.target.value })} className="rounded-xl" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Welcome Text</label>
              <Textarea data-testid="home-welcome-text" value={home.welcome_text} onChange={(e) => setHome({ ...home, welcome_text: e.target.value })} className="rounded-xl" rows={4} />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-3">Welcome Section Image</label>
              <div className="border border-[#E2E8F0] rounded-2xl p-4 md:p-5 bg-[#F8FAFC]/60">
                {home.welcome_image ? (
                  <div className="flex flex-col md:flex-row gap-5 md:items-start">
                    <img
                      src={resolveImageUrl(home.welcome_image)}
                      alt="Welcome section preview"
                      className="w-full md:w-56 h-40 object-cover rounded-xl border border-[#E2E8F0] bg-white"
                    />
                    <div className="space-y-3">
                      <p className="text-sm text-[#475569]">This image appears beside the Welcome section on the homepage.</p>
                      <div className="flex flex-wrap gap-3">
                        <label
                          data-testid="welcome-image-upload-btn"
                          className={`inline-flex items-center gap-2 bg-white border border-[#E2E8F0] rounded-xl px-4 py-2.5 cursor-pointer hover:bg-[#EBF2FA] text-sm ${uploadingWelcomeImage ? 'opacity-60' : ''}`}
                        >
                          <Upload size={16} className="text-[#475569]" />
                          {uploadingWelcomeImage ? 'Uploading...' : 'Upload New Image'}
                          <input type="file" accept="image/*" className="hidden" onChange={handleWelcomeImageUpload} disabled={uploadingWelcomeImage} />
                        </label>
                        <Button
                          type="button"
                          variant="outline"
                          data-testid="welcome-image-remove-btn"
                          onClick={handleRemoveWelcomeImage}
                          className="rounded-xl text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                        >
                          <X size={16} className="mr-2" /> Remove Image
                        </Button>
                      </div>
                      <p className="text-xs text-[#94A3B8]">Recommended: landscape image, at least 1200px wide.</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="rounded-2xl border border-dashed border-[#CBD5E1] bg-white px-4 py-8 text-center">
                      <p className="text-sm font-medium text-[#0F172A]">No Welcome image selected</p>
                      <p className="text-xs text-[#94A3B8] mt-1">The homepage will switch to a balanced text-only layout until you add one.</p>
                    </div>
                    <label
                      data-testid="welcome-image-upload-btn"
                      className={`inline-flex items-center gap-2 bg-white border border-[#E2E8F0] rounded-xl px-4 py-2.5 cursor-pointer hover:bg-[#EBF2FA] text-sm ${uploadingWelcomeImage ? 'opacity-60' : ''}`}
                    >
                      <Upload size={16} className="text-[#475569]" />
                      {uploadingWelcomeImage ? 'Uploading...' : 'Upload Welcome Image'}
                      <input type="file" accept="image/*" className="hidden" onChange={handleWelcomeImageUpload} disabled={uploadingWelcomeImage} />
                    </label>
                  </div>
                )}
              </div>
            </div>

            <hr className="border-[#E2E8F0]" />
            <h2 className="text-lg font-bold text-[#0F172A]">Core Values</h2>
            {(home.values || []).map((val, i) => (
              <div key={i} className="border border-[#E2E8F0] rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#475569]">Value {i + 1}</span>
                  <Button variant="ghost" size="sm" onClick={() => {
                    const newVals = [...home.values];
                    newVals.splice(i, 1);
                    setHome({ ...home, values: newVals });
                  }}><Trash2 size={14} className="text-red-500" /></Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Title" value={val.title} onChange={(e) => {
                    const v = [...home.values]; v[i] = { ...v[i], title: e.target.value }; setHome({ ...home, values: v });
                  }} className="rounded-lg" />
                  <Input placeholder="Icon (heart, users, etc)" value={val.icon} onChange={(e) => {
                    const v = [...home.values]; v[i] = { ...v[i], icon: e.target.value }; setHome({ ...home, values: v });
                  }} className="rounded-lg" />
                </div>
                <Textarea placeholder="Description" value={val.description} onChange={(e) => {
                  const v = [...home.values]; v[i] = { ...v[i], description: e.target.value }; setHome({ ...home, values: v });
                }} className="rounded-lg" rows={2} />
              </div>
            ))}
            <Button variant="outline" onClick={() => setHome({ ...home, values: [...(home.values || []), { icon: 'star', title: '', description: '' }] })} className="rounded-xl">
              <Plus size={16} className="mr-2" /> Add Value
            </Button>

            <div className="flex justify-end pt-4">
              <Button data-testid="save-home-content" onClick={() => saveSection('home', home)} disabled={saving === 'home'}
                className="bg-[#1E4FA1] hover:bg-[#153A7A] text-white rounded-xl px-6">
                <Save size={16} className="mr-2" /> {saving === 'home' ? 'Saving...' : 'Save Home Content'}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* ABOUT PAGE */}
        <TabsContent value="about">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 space-y-6">
            <h2 className="text-lg font-bold text-[#0F172A]">Mission</h2>
            <Input data-testid="about-mission-title" value={about.mission_title} onChange={(e) => setAbout({ ...about, mission_title: e.target.value })} className="rounded-xl" />
            <Textarea data-testid="about-mission-text" value={about.mission_text} onChange={(e) => setAbout({ ...about, mission_text: e.target.value })} className="rounded-xl" rows={3} />

            <h2 className="text-lg font-bold text-[#0F172A]">Vision</h2>
            <Input data-testid="about-vision-title" value={about.vision_title} onChange={(e) => setAbout({ ...about, vision_title: e.target.value })} className="rounded-xl" />
            <Textarea data-testid="about-vision-text" value={about.vision_text} onChange={(e) => setAbout({ ...about, vision_text: e.target.value })} className="rounded-xl" rows={3} />

            <h2 className="text-lg font-bold text-[#0F172A]">Core Values</h2>
            {(about.values || []).map((val, i) => (
              <div key={i} className="border border-[#E2E8F0] rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#475569]">Value {i + 1}</span>
                  <Button variant="ghost" size="sm" onClick={() => {
                    const newVals = [...about.values]; newVals.splice(i, 1); setAbout({ ...about, values: newVals });
                  }}><Trash2 size={14} className="text-red-500" /></Button>
                </div>
                <Input placeholder="Title" value={val.title} onChange={(e) => {
                  const v = [...about.values]; v[i] = { ...v[i], title: e.target.value }; setAbout({ ...about, values: v });
                }} className="rounded-lg" />
                <Textarea placeholder="Description" value={val.description} onChange={(e) => {
                  const v = [...about.values]; v[i] = { ...v[i], description: e.target.value }; setAbout({ ...about, values: v });
                }} className="rounded-lg" rows={2} />
              </div>
            ))}
            <Button variant="outline" onClick={() => setAbout({ ...about, values: [...(about.values || []), { title: '', description: '' }] })} className="rounded-xl">
              <Plus size={16} className="mr-2" /> Add Value
            </Button>

            <div className="flex justify-end pt-4">
              <Button data-testid="save-about-content" onClick={() => saveSection('about', about)} disabled={saving === 'about'}
                className="bg-[#1E4FA1] hover:bg-[#153A7A] text-white rounded-xl px-6">
                <Save size={16} className="mr-2" /> {saving === 'about' ? 'Saving...' : 'Save About Content'}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* STATEMENT OF FAITH */}
        <TabsContent value="faith">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Page Title</label>
              <Input data-testid="faith-title" value={faith.title} onChange={(e) => setFaith({ ...faith, title: e.target.value })} className="rounded-xl" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Introduction</label>
              <Textarea data-testid="faith-intro" value={faith.intro} onChange={(e) => setFaith({ ...faith, intro: e.target.value })} className="rounded-xl" rows={3} />
            </div>

            <h2 className="text-lg font-bold text-[#0F172A]">Faith Items</h2>
            {(faith.items || []).map((item, i) => (
              <div key={i} className="border border-[#E2E8F0] rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#475569]">Item {item.number}</span>
                  <Button variant="ghost" size="sm" onClick={() => {
                    const items = [...faith.items]; items.splice(i, 1); setFaith({ ...faith, items });
                  }}><Trash2 size={14} className="text-red-500" /></Button>
                </div>
                <Input placeholder="Title" value={item.title} onChange={(e) => {
                  const items = [...faith.items]; items[i] = { ...items[i], title: e.target.value }; setFaith({ ...faith, items });
                }} className="rounded-lg" />
                <Textarea placeholder="Full text" value={item.text} onChange={(e) => {
                  const items = [...faith.items]; items[i] = { ...items[i], text: e.target.value }; setFaith({ ...faith, items });
                }} className="rounded-lg" rows={3} />
              </div>
            ))}
            <Button variant="outline" onClick={() => {
              const num = (faith.items || []).length + 1;
              setFaith({ ...faith, items: [...(faith.items || []), { number: num, title: '', text: '' }] });
            }} className="rounded-xl">
              <Plus size={16} className="mr-2" /> Add Faith Item
            </Button>

            <div className="flex justify-end pt-4">
              <Button data-testid="save-faith-content" onClick={() => saveSection('faith', faith)} disabled={saving === 'faith'}
                className="bg-[#1E4FA1] hover:bg-[#153A7A] text-white rounded-xl px-6">
                <Save size={16} className="mr-2" /> {saving === 'faith' ? 'Saving...' : 'Save Statement of Faith'}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* ACTIVITIES */}
        <TabsContent value="activities">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Page Title</label>
              <Input
                data-testid="activities-title"
                value={activities.title}
                onChange={(e) => setActivities({ ...activities, title: e.target.value })}
                className="rounded-xl"
              />
            </div>

            <h2 className="text-lg font-bold text-[#0F172A]">Annual Activities</h2>
            {(activities.activities || []).map((activity, i) => (
              <div key={i} className="border border-[#E2E8F0] rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#475569]">Activity {i + 1}</span>
                  <Button variant="ghost" size="sm" onClick={() => {
                    const next = [...(activities.activities || [])];
                    next.splice(i, 1);
                    setActivities({ ...activities, activities: next });
                  }}><Trash2 size={14} className="text-red-500" /></Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <Input placeholder="Month label" value={activity.month || ''} onChange={(e) => updateActivity(i, 'month', e.target.value)} className="rounded-lg" />
                  <Input placeholder="Day" value={activity.day || ''} onChange={(e) => updateActivity(i, 'day', e.target.value)} className="rounded-lg" />
                  <Input placeholder="Icon" value={activity.icon || ''} onChange={(e) => updateActivity(i, 'icon', e.target.value)} className="rounded-lg" />
                  <Input placeholder="Date" value={activity.date || ''} onChange={(e) => updateActivity(i, 'date', e.target.value)} className="rounded-lg" />
                </div>
                <Input placeholder="Title" value={activity.title || ''} onChange={(e) => updateActivity(i, 'title', e.target.value)} className="rounded-lg" />
                <Textarea placeholder="Detail" value={activity.detail || ''} onChange={(e) => updateActivity(i, 'detail', e.target.value)} className="rounded-lg" rows={2} />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input placeholder="Registration Period" value={activity.registration_period || ''} onChange={(e) => updateActivity(i, 'registration_period', e.target.value)} className="rounded-lg" />
                  <Input placeholder="Big Final" value={activity.big_final || ''} onChange={(e) => updateActivity(i, 'big_final', e.target.value)} className="rounded-lg" />
                  <Input placeholder="Location" value={activity.location || ''} onChange={(e) => updateActivity(i, 'location', e.target.value)} className="rounded-lg" />
                </div>
              </div>
            ))}
            <Button
              variant="outline"
              onClick={() => setActivities({
                ...activities,
                activities: [...(activities.activities || []), { icon: 'calendar', month: '', day: '', title: '', date: '', detail: '' }],
              })}
              className="rounded-xl"
            >
              <Plus size={16} className="mr-2" /> Add Activity
            </Button>

            <div className="flex justify-end pt-4">
              <Button data-testid="save-activities-content" onClick={() => saveSection('activities', activities)} disabled={saving === 'activities'}
                className="bg-[#1E4FA1] hover:bg-[#153A7A] text-white rounded-xl px-6">
                <Save size={16} className="mr-2" /> {saving === 'activities' ? 'Saving...' : 'Save Activities'}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
